import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import CreditsPlanModal from './layout/CreditsPlanModal';

// Definizioni colori per coerenza
const darkPurple = '#1a0c2e';
const accentPink = '#ff40c0';
const textLight = '#f0f0f0';
const subtleBorderLogin = 'rgba(240, 240, 240, 0.35)'; // Bordo per bottoni Login/Register, più visibile
const hoverBackgroundLogin = 'rgba(255, 255, 255, 0.1)'; // Sfondo hover per Login/Register

const NavContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 25px;
  background-color: ${darkPurple};
  position: sticky;
  top: 0;
  z-index: 1000;
  border-bottom: 1px solid ${accentPink};
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3), 0 0 25px rgba(255, 64, 192, 0.15);
  overflow: visible;
  
  @media (max-width: 768px) {
    padding: 10px 15px;
  }
`;

const Logo = styled(Link)`
  font-family: 'Bubblegum Sans', 'Poppins', sans-serif;
  font-size: 1.9rem;
  font-weight: 700;
  background: linear-gradient(45deg, #FF40C0, #FFB86C);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-decoration: none;
  display: flex;
  align-items: center;
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.03);
  }
  
  @media (max-width: 768px) {
    font-size: 1.6rem;
  }
  
  &::after {
    content: "❤️";
    margin-left: 5px;
    font-size: 1.1rem;
    -webkit-text-fill-color: initial;
    color: #ff40c0;
    
    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const UserCreditsDisplay = styled.span`
  color: #1a0c2e;
  font-size: 0.85rem;
  font-weight: 600;
  padding: 8px 15px;
  background: linear-gradient(45deg, #FFD700, #FFA500); 
  border-radius: 20px;
  margin-left: 5px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  box-shadow: 
    0 3px 10px rgba(255, 215, 0, 0.25),
    0 0 15px rgba(255, 215, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;

  &:hover {
    transform: translateY(-2px) scale(1.02);
    box-shadow: 
      0 5px 14px rgba(255, 215, 0, 0.3),
      0 0 20px rgba(255, 215, 0, 0.18);
  }
`;

// Stile per il bottone Login (giallo/arancione acceso con più personalità)
const LoginButton = styled.button`
  padding: 10px 24px;
  background: linear-gradient(45deg, #FFA500, #FFD700);
  color: #1a0c2e;
  border: none;
  border-radius: 25px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.35s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 3px 10px rgba(255, 165, 0, 0.3),
              0 0 15px rgba(255, 165, 0, 0.1);
  position: relative;
  overflow: hidden;
  
  &:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 5px 15px rgba(255, 165, 0, 0.4),
                0 0 20px rgba(255, 165, 0, 0.2);
    color: #000;
  }
  
  &:active {
    transform: translateY(1px);
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(45deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0));
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &:hover::after {
    opacity: 1;
  }
`;

// Stile per il bottone Register (stessa base del Login ma con colori diversi)
const RegisterButton = styled(LoginButton)`
  background: linear-gradient(45deg, #ff40c0, #ff65d5);
  color: white;
  box-shadow: 0 3px 10px rgba(255, 64, 192, 0.3),
              0 0 15px rgba(255, 64, 192, 0.1);
  margin-left: 12px;
  
  &:hover {
    box-shadow: 0 5px 15px rgba(255, 64, 192, 0.4),
                0 0 20px rgba(255, 64, 192, 0.2);
    color: white;
  }
`;

// Nuovo stile per il pulsante Logout (seguendo lo stile dell'immagine)
const LogoutStyledButton = styled.button`
  padding: 8px 20px;
  background: linear-gradient(45deg, #ff40c0, #ff65d5);
  color: white;
  border: none;
  border-radius: 22px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: 0 4px 12px rgba(255, 64, 192, 0.4);
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(255, 64, 192, 0.55);
  }
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
    padding: 10px 15px;
  }
`;

const MobileMenuIcon = styled.div`
  display: none;
  font-size: 1.8rem;
  color: ${textLight};
  cursor: pointer;
  padding: 5px;

  @media (max-width: 768px) {
    display: block;
  }
`;

const MobileMenuPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 12px;
  background-color: ${darkPurple};
  border-top: 1px solid ${accentPink};
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.2);
  padding: 15px;
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  z-index: 999;
  max-height: 0;
  opacity: 0;
  overflow: hidden;
  pointer-events: none;
  transition: max-height 0.35s ease-in-out, opacity 0.35s ease-in-out;

  &.open {
    max-height: 500px; 
    opacity: 1;
    pointer-events: auto;
  }

  ${UserCreditsDisplay} {
    margin-left: 0;
    width: 100%;
    justify-content: center;
    padding: 10px 15px;
    font-size: 0.9rem;
  }

  ${LoginButton}, ${LogoutStyledButton} {
    width: 100%;
    justify-content: center;
    padding: 10px 15px;
    font-size: 0.9rem;
    background-color: rgba(255,255,255,0.03);
    border-width: 1px;
    &:hover {
        background-color: rgba(255,255,255,0.08);
    }
  }
  
  .mobile-nav-link {
    width: 100%;
    justify-content: flex-start;
    padding: 10px 15px;
    font-size: 0.9rem;
    background-color: transparent;
    border: none;
    color: ${textLight};
    text-align: left;
     &:hover {
        background-color: rgba(255,255,255,0.08);
    }
  }
`;

// Icona Logout (right-from-bracket di Font Awesome)
const RightFromBracketIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 512 512" style={{ marginRight: '4px' }}>
    <path d="M377.9 105.9L500.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L377.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1-128 0c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM160 96L96 96c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-64 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32z" />
  </svg>
);

// Icona Monetine (coins di Font Awesome)
const FontAwesomeCoinsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" viewBox="0 0 512 512" style={{ marginRight: '6px' }}>
    <path d="M512 80c0 18-14.3 34.6-38.4 48c-29.1 16.1-72.5 27.5-122.3 30.9c-3.7-1.8-7.4-3.5-11.3-5C300.6 137.4 248.2 128 192 128c-8.3 0-16.4 .2-24.5 .6l-1.1-.6C142.3 114.6 128 98 128 80c0-44.2 86-80 192-80S512 35.8 512 80zM160.7 161.1c10.2-.7 20.7-1.1 31.3-1.1c62.2 0 117.4 12.3 152.5 31.4C369.3 204.9 384 221.7 384 240c0 4-.7 7.9-2.1 11.7c-4.6 13.2-17 25.3-35 35.5c0 0 0 0 0 0c-.1 .1-.3 .1-.4 .2l0 0c-.3 .2-.6 .3-.9 .5c-35 19.4-90.8 32-153.6 32c-59.6 0-112.9-11.3-148.2-29.1c-1.9-.9-3.7-1.9-5.5-2.9C14.3 274.6 0 258 0 240c0-34.8 53.4-64.5 128-75.4c10.5-1.5 21.4-2.7 32.7-3.5zM416 240c0-21.9-10.6-39.9-24.1-53.4c28.3-4.4 54.2-11.4 76.2-20.5c16.3-6.8 31.5-15.2 43.9-25.5V176c0 19.3-16.5 37.1-43.8 50.9c-14.6 7.4-32.4 13.7-52.4 18.5c.1-1.8 .2-3.5 .2-5.3zm-32 96c0 18-14.3 34.6-38.4 48c-1.8 1-3.6 1.9-5.5 2.9C304.9 404.7 251.6 416 192 416c-62.8 0-118.6-12.6-153.6-32C14.3 370.6 0 354 0 336V300.6c12.5 10.3 27.6 18.7 43.9 25.5C83.4 342.6 135.8 352 192 352s108.6-9.4 148.1-25.9c7.8-3.2 15.3-6.9 22.4-10.9c6.1-3.4 11.8-7.2 17.2-11.2c1.5-1.1 2.9-2.3 4.3-3.4V336zm32 0V276.5c20-4.8 37.8-11.1 52.4-18.5c27.3-13.8 43.8-31.6 43.8-50.9v35.4c-12.5 10.3-27.6 18.7-43.9 25.5c-22 9.1-47.9 16.1-76.2 20.5c13.5 13.5 24.1 31.5 24.1 53.4c0 1.8-.1 3.5-.2 5.3c20 4.8 37.8 11.1 52.4 18.5c27.3 13.8 43.8 31.6 43.8 50.9V336c0 18-14.3 34.6-38.4 48c-8.8 4.9-19.3 9.7-31.3 13.7c1.2-3.2 1.8-6.5 1.8-9.8zM192 448c56.2 0 108.6-9.4 148.1-25.9c16.3-6.8 31.5-15.2 43.9-25.5V432c0 44.2-86 80-192 80S0 476.2 0 432V396.6c12.5 10.3 27.6 18.7 43.9 25.5C83.4 438.6 135.8 448 192 448z"/>
  </svg>
);

const NavbarSimple = () => {
  const { currentUser, logoutContext, loading } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef(null);
  const iconRef = useRef(null);

  const handleAddCredits = () => {
    setShowModal(true);
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };
  
  const handleLogout = async () => {
    await logoutContext();
    navigate('/login');
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const handleLogin = () => {
    navigate('/login');
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };

  const handleRegister = () => {
    navigate('/register');
    if (isMobileMenuOpen) setIsMobileMenuOpen(false);
  };
  
  const handleNavigate = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  }

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target) && 
          iconRef.current && !iconRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);
  
  if (loading) {
    return null; 
  }

  // Configurazione responsive per i button nel menu mobile
  const mobileButtonStyle = {
    width: '100%', 
    marginBottom: '12px',
    padding: '12px',
    fontSize: '1rem'
  };
  
  const mobileRegisterStyle = {
    ...mobileButtonStyle, 
    marginLeft: 0
  };

  return (
    <>
      <NavContainer>
        <Logo 
          to={currentUser ? (location.pathname === '/dashboard' ? '/' : '/dashboard') : '/'}
        >
          InstaRizz
        </Logo>
        
        <UserInfoDesktop>
          {currentUser ? (
            <>
              <UserCreditsDisplay onClick={handleAddCredits} title="View credit plans">
                <FontAwesomeCoinsIcon />
                {currentUser.credits !== undefined ? currentUser.credits : '0'}
              </UserCreditsDisplay>
              <LogoutStyledButton onClick={handleLogout}>
                <RightFromBracketIcon />
                Logout
              </LogoutStyledButton>
            </>
          ) : (
            <>
              <LoginButton onClick={handleLogin}>Login</LoginButton>
              <RegisterButton onClick={handleRegister}>Register</RegisterButton>
            </>
          )}
        </UserInfoDesktop>

        <MobileMenuIcon ref={iconRef} onClick={toggleMobileMenu}>
          {isMobileMenuOpen ? '✕' : '☰'} 
        </MobileMenuIcon>
        
        <MobileMenuPanel ref={menuRef} className={isMobileMenuOpen ? 'open' : ''}>
          {currentUser ? (
            <>
              <UserCreditsDisplay onClick={handleAddCredits}>
                <FontAwesomeCoinsIcon />
                Credits: {currentUser.credits !== undefined ? currentUser.credits : '0'}
              </UserCreditsDisplay>
              <button className="mobile-nav-link" onClick={() => handleNavigate('/dashboard')}>Dashboard</button>
              <button className="mobile-nav-link" onClick={() => handleNavigate('/analyze')}>New Analysis</button>
              <button className="mobile-nav-link" onClick={() => handleNavigate('/history')}>History</button>
              <LogoutStyledButton onClick={handleLogout}>
                <RightFromBracketIcon />
                Logout
              </LogoutStyledButton>
            </>
          ) : (
            <>
              <LoginButton onClick={handleLogin} style={mobileButtonStyle}>Login</LoginButton>
              <RegisterButton onClick={handleRegister} style={mobileRegisterStyle}>Register</RegisterButton>
            </>
          )}
        </MobileMenuPanel>
      </NavContainer>
      
      {showModal && (
        <CreditsPlanModal onClose={() => setShowModal(false)} />
      )}
    </>
  );
};

export default NavbarSimple;

const UserInfoDesktop = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  @media (max-width: 768px) {
    display: none; 
  }
`; 